import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { dirtyRegistry } from "@/lib/persistence/dirty-registry";

interface GuardContextValue {
  /**
   * Run `action` immediately if no forms are dirty; otherwise show the
   * Continue Editing / Save & Close / Discard prompt before proceeding.
   */
  guard: (action: () => void) => void;
}

const GuardContext = createContext<GuardContextValue | null>(null);

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const dirtyEntriesRef = useRef<ReturnType<typeof dirtyRegistry.list>>([]);

  const close = () => {
    setOpen(false);
    pendingActionRef.current = null;
  };

  const guard = useCallback((action: () => void) => {
    const dirty = dirtyRegistry.list().filter((e) => {
      try {
        return e.isDirty();
      } catch {
        return false;
      }
    });
    if (dirty.length === 0) {
      action();
      return;
    }
    dirtyEntriesRef.current = dirty;
    pendingActionRef.current = action;
    setOpen(true);
  }, []);

  const handleSaveAndClose = async () => {
    setBusy(true);
    try {
      for (const e of dirtyEntriesRef.current) {
        if (e.save) {
          await e.save();
        }
      }
      pendingActionRef.current?.();
    } finally {
      setBusy(false);
      close();
    }
  };

  const handleDiscard = () => {
    dirtyEntriesRef.current.forEach((e) => e.discard?.());
    pendingActionRef.current?.();
    close();
  };

  const value = useMemo(() => ({ guard }), [guard]);
  const labels = dirtyEntriesRef.current.map((e) => e.label).join(", ");

  return (
    <GuardContext.Provider value={value}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => !o && !busy && close()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              {labels ? `Unsaved work in: ${labels}. ` : ""}
              Save your changes, discard them, or keep editing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={busy}>Continue editing</AlertDialogCancel>
            <Button variant="ghost" onClick={handleDiscard} disabled={busy}>
              Discard
            </Button>
            <AlertDialogAction onClick={handleSaveAndClose} disabled={busy}>
              {busy ? "Saving…" : "Save & close"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </GuardContext.Provider>
  );
}

/**
 * Returns a `guard` function that wraps any close/navigate action with the
 * unsaved-changes prompt. Safe to call from anywhere inside the provider.
 */
export function useUnsavedGuard(): GuardContextValue["guard"] {
  const ctx = useContext(GuardContext);
  if (!ctx) {
    // No provider → degrade gracefully (just run the action).
    return (action: () => void) => action();
  }
  return ctx.guard;
}
